// services/dailyReport.ts
import api from './api';
import { ReportData, ReportListItem, ReportListResponse, DailyReportSearchParams, PrefillResponse } from '../types/dailyReport';
import { CalendarResponse } from '../types/calendar';
import { ManpowerCategoryResponse } from '../types/manpowerCategory';

export const dailyReportService = {
  create: async (data: ReportData): Promise<ReportData> => {
    const response = await api.post('/daily-report/create', data);
    return response.data;
  },

  update: async (id: number, data: ReportData): Promise<ReportData> => {
    const response = await api.put(`/daily-report/${id}`, data);
    return response.data;
  },

  // 日報詳細を取得
  getById: async (id: number): Promise<ReportData> => {
    try {
      const response = await api.get<ReportData>(`/daily-report/${id}`);

      if (!response.data) {
        throw new Error('日報詳細の取得に失敗しました');
      }

      return response.data;
    } catch (error) {
      console.error('日報詳細の取得に失敗:', error);
      throw error;
    }
  },

  // 前回日報からのプリフィルデータを取得
  // - 過去日報なし: null
  // - reportId未指定: 最新日報からプリフィル
  // - reportId指定: 該当日報からプリフィル
  getPrefill: async (constructionId: number, reportId?: number): Promise<PrefillResponse | null> => {
    try {
      const url = `/daily-report/construction/${constructionId}/prefill`;
      const config = reportId !== undefined ? { params: { reportId } } : undefined;
      const response = await api.get<PrefillResponse>(url, config);
      if (response.status === 204) return null;
      return response.data;
    } catch (error: any) {
      // 指定reportIdが見つからない等は404、空状態は204で返るのでそのまま握りつぶさず投げる
      if (error?.response?.status === 204) return null;
      console.error('プリフィルデータの取得に失敗:', error);
      throw error;
    }
  },

  // 特定の工事IDに関連する日報一覧を取得
  getByConstructionId: async (constructionId: number): Promise<ReportListItem[]> => {
    try {
      const response = await api.get<{reports: ReportListItem[], total: number}>(
        `/daily-report/construction/${constructionId}`
      );

      if (!response.data || !response.data.reports) {
        throw new Error('日報データの取得に失敗しました');
      }
      
      return response.data.reports;
    } catch (error) {
      console.error('日報一覧の取得に失敗:', error);
      throw error;
    }
  },

  // 日報一覧を全件取得
  getAll: async (): Promise<{ reports: ReportListItem[]; total: number }> => {
    const response = await api.get('/daily-report/list');
    return response.data;
  },

  // 検索条件に基づいた日報一覧を取得 (新規追加)
  search: async (params: DailyReportSearchParams): Promise<{ reports: ReportListItem[]; total: number }> => {
    try {
      // URLクエリパラメータを構築
      const queryParams = new URLSearchParams();
      
      // 空でないパラメータのみをクエリに追加
      Object.entries(params).forEach(([key, value]) => {
        if (value) {
          queryParams.append(key, value);
        }
      });
      
      // 検索APIを呼び出し
      const response = await api.get(`/daily-report/search?${queryParams.toString()}`);
      
      if (!response.data) {
        throw new Error('日報の検索に失敗しました');
      }
      
      return response.data;
    } catch (error) {
      console.error('日報検索エラー:', error);
      throw error;
    }
  },

  delete: async (id: number): Promise<void> => {
    await api.delete(`/daily-report/${id}`);
  },

  isWorkingDay: async (date: string): Promise<boolean> => {
    try {
      // 日付を YYYY-MM-DD 形式に変換
      let formattedDate = date;
      
      // 日付文字列にTが含まれる場合（ISO形式の可能性）
      if (date.includes('T')) {
        const dateObj = new Date(date);
        formattedDate = `${dateObj.getFullYear()}-${String(dateObj.getMonth() + 1).padStart(2, '0')}-${String(dateObj.getDate()).padStart(2, '0')}`;
      }
      
      const response = await api.get(`/calendar/check-working-day/${formattedDate}`);
      return response.data.isWorkingDay;
    } catch (error) {
      console.error('稼働日チェックエラー:', error);
      // エラーの場合はデフォルトで平日とする
      return true;
    }
  },

  getManpowerCategory: async (id: number): Promise<ManpowerCategoryResponse> => {
    try {
      const response = await api.get<ManpowerCategoryResponse>(`/manpower-category/${id}`);
      return response.data;
    } catch (error) {
      console.error('単価カテゴリ取得エラー:', error);
      throw error;
    }
  }
};